<template lang="html">
  <v-chip
    small
    :light="isLight"
    :dark="!isLight"
    :color="currentStatus.color"
    :outlined="currentStatus.color ? currentStatus.outlined : false"
  >
    <!-- if disabled, outlined variant shouldn't be used -->
    <v-icon :left="$vuetify.breakpoint.mdAndUp && mode != 'index'" small>
      {{ currentStatus.icon }}
    </v-icon>
    <span class="text-truncate" :class="{'hidden-sm-and-down': mode == 'index'}">
      {{ formattedText }}
    </span>
  </v-chip>
</template>

<script>
import upperFirst from 'lodash/upperFirst';
import { isLightColor } from '@/functions/isLightColor';

/** Statuses
 *  Explanation on making a correct status (TODO: move to design document)
 
 *  1. Color: ['successAccent', 'warningAccent', 'errorAccent', 'info', undefined]
    Color describes the overall state of the process, whether it's going well (towards a planned final state), or not (cancelled prematurely, for example)

    - Success - Good, requires no interaction
    - Info - Good, requires interaction, not blocking
    - Warning - Good, requires interaction, blocking
    - Error - Bad
    - undefined - Not Active / Neutral (defaults to lightgrey)

 *  2. Icon: ['mdi-*']
    The Icon should describe the status or the object the status was made for, should be an MDI icon for now.

 *  3. Outlined: [true, false]
    Outlined describes whether this state describes a process that is finished (like an approved hour report), or ongoing (like an active project).

    - true - Process is finished / long-lasting status
    - false - Process is ongoing / short-lasting status

    undefined color is never outlined due to clash with design system / lack of clarity of a thin lightgrey line.
 */

const STATUSES = {
  accepted: {
    color: 'successAccent',
    icon: 'mdi-account-check-outline',
    outlined: false,
  },
  active: {
    color: 'successAccent',
    icon: 'mdi-check',
    outlined: false,
  },
  approved: {
    color: 'successAccent',
    icon: 'mdi-check-circle-outline',
    outlined: true,
  },
  archived: {
    color: 'successAccent',
    icon: 'mdi-archive-arrow-down-outline',
    outlined: true,
  },
  arrived: {
    color: 'warningAccent',
    icon: 'mdi-ferry',
    outlined: true,
  },
  berthed: {
    color: 'info',
    icon: 'mdi-ferry',
    outlined: true,
  },
  blocked: {
    color: 'warningAccent',
    icon: 'mdi-timer-sand',
    outlined: false,
  },
  cancelled: {
    color: 'errorAccent',
    icon: 'mdi-window-close',
    outlined: true,
  },
  completed: {
    color: 'successAccent',
    icon: 'mdi-check',
    outlined: true,
  },
  date_end_exceeded: {
    color: 'successAccent',
    icon: 'mdi-clock-end',
    outlined: true,
  },
  delivered: {
    color: 'warningAccent',
    icon: 'mdi-eye',
    outlined: true,
  },
  draft: {
    color: 'info',
    icon: 'mdi-pencil-lock-outline',
    outlined: false,
  },
  departed: {
    color: 'successAccent',
    icon: 'mdi-ferry',
    outlined: true,
  },
  exit: {
    color: 'errorAccent',
    icon: 'mdi-window-close',
    outlined: true,
  },
  expected: {
    color: 'info',
    icon: 'mdi-clock',
    outlined: true,
  },
  expired: {
    color: 'errorAccent',
    icon: 'mdi-clock-alert',
    outlined: true,
  },
  exported: {
    color: 'successAccent',
    icon: 'mdi-check',
    outlined: true,
  },
  finished: {
    color: 'successAccent',
    icon: 'mdi-check',
    outlined: true,
  },
  maintenance: {
    color: 'warningAccent',
    icon: 'mdi-hammer-wrench',
    outlined: false,
  },
  needs_change: {
    color: 'warningAccent',
    icon: 'mdi-timer-sand',
    outlined: false,
  },
  not_validated: {
    color: 'warningAccent',
    icon: 'mdi-timer-sand',
    outlined: false,
  },
  on_hold: {
    color: 'info',
    icon: 'mdi-timer-sand',
    outlined: false,
  },
  open: {
    color: 'warningAccent',
    icon: 'mdi-timer-sand',
    outlined: false,
  },
  passive: {
    color: 'info',
    icon: 'mdi-timer-sand',
    outlined: false,
  },
  pending: {
    color: 'warningAccent',
    icon: 'mdi-timer-sand',
    outlined: false,
  },
  pilot: {
    color: 'warningAccent',
    icon: 'mdi-timer-sand',
    outlined: false,
  },
  proposed: {
    color: 'warningAccent',
    icon: 'mdi-timer-sand',
    outlined: false,
  },
  processing: {
    color: 'warningAccent',
    icon: 'mdi-timer-sand',
    outlined: false,
  },
  recalled: {
    color: 'errorAccent',
    icon: 'mdi-window-close',
    outlined: false,
  },
  reference: {
    color: 'info',
    icon: 'mdi-timer-sand',
    outlined: true,
  },
  rejected: {
    color: 'errorAccent',
    icon: 'mdi-window-close',
    outlined: true,
  },
  renewal_planned: {
    color: 'warningAccent',
    icon: 'mdi-timer-sand',
    outlined: true,
  },
  renewed: {
    color: 'successAccent',
    icon: 'mdi-check',
    outlined: true,
  },
  valid: {
    color: 'successAccent',
    icon: 'mdi-check',
    outlined: false,
  },
  waiting_for_certificate: {
    color: 'warningAccent',
    icon: 'mdi-timer-sand',
    outlined: false,
  },
  not_started: {
    color: 'warningAccent',
    icon: 'mdi-calendar-start',
    outlined: true,
  },
  published: {
    color: 'successAccent',
    icon: 'mdi-eye-check',
    outlined: true,
  },
  unpublished: {
    color: 'warningAccent',
    icon: 'mdi-eye-off',
    outlined: true,
  },
  skipped: {
    color: 'info',
    icon: 'mdi-debug-step-over',
    outlined: true,
  },
};

export default {
  props: {
    status: {
      type: String,
      validator: v => v === undefined || STATUSES[v] !== undefined,
      default: undefined,
    },
    text: {
      type: String,
      default: '',
    },
    color: {
      type: String,
      default: undefined,
    },
    icon: {
      type: String,
      default: '',
    },
    outlined: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: undefined,
    }
  },
  computed: {
    currentStatus() {
      if (this.status) return STATUSES[this.status];

      const { color, icon, outlined } = this;
      return {
        color,
        icon,
        outlined,
      };
    },
    formattedText() {
      if (this.status) return upperFirst(this.$t(`statusses.${this.status}`));
      return this.text;
    },
    isLight() {
      if (!this.currentStatus) return false;
      const color = this.currentStatus.color;
      return isLightColor(color);
    },
  },
};
</script>

<style lang="scss" scoped></style>
